import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";

import { useCreateAgreement } from "../hooks/useEmployeeAgreements";

interface Props {
  open: boolean;
  onClose: () => void;
  employees: { id: string; full_name: string }[];
}

export function CreateAgreementDialog({ open, onClose, employees }: Props) {
  const [employee, setEmployee] = useState("");
  const [title, setTitle] = useState("");
  const [signedDate, setSignedDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [validUntil, setValidUntil] = useState("");
  const [isB2B, setIsB2B] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyNip, setCompanyNip] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useCreateAgreement();

  function submit() {
    setError(null);
    mutate(
      {
        employee,
        title,
        signed_date: signedDate,
        valid_until: validUntil || null,
        is_b2b: isB2B,
        company_name: isB2B ? companyName : "",
        company_nip: isB2B ? companyNip : "",
        notes,
      },
      {
        onSuccess: () => {
          onClose();
          setEmployee("");
          setTitle("");
          setValidUntil("");
          setIsB2B(false);
          setCompanyName("");
          setCompanyNip("");
          setNotes("");
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { errors?: unknown } } })?.response
              ?.data?.errors;
          setError(
            typeof message === "string"
              ? message
              : "Nie udało się utworzyć UR.",
          );
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Nowa UR pracownik-firma" size="md">
      <div className="flex flex-col gap-3">
        <label className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
          Pracownik
        </label>
        <select
          value={employee}
          onChange={(e) => setEmployee(e.target.value)}
          className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">— wybierz —</option>
          {employees.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name}
            </option>
          ))}
        </select>

        <Input
          label="Tytuł umowy"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="agreement-signed-date" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Data podpisania
            </label>
            <DatePicker id="agreement-signed-date" value={signedDate} onChange={setSignedDate} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="agreement-valid-until" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Data ważności
            </label>
            <DatePicker id="agreement-valid-until" value={validUntil} onChange={setValidUntil} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isB2B}
            onChange={(e) => setIsB2B(e.target.checked)}
          />
          Umowa B2B (pracownik fakturuje GK jako własna firma)
        </label>

        {isB2B && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nazwa firmy"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
            <Input
              label="NIP firmy"
              value={companyNip}
              onChange={(e) => setCompanyNip(e.target.value)}
            />
          </div>
        )}

        <label className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
          Uwagi
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm min-h-[80px]"
        />

        {error && (
          <div className="text-sm text-red-500 dark:text-red-400">{error}</div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            onClick={submit}
            disabled={isPending || !employee || !title.trim()}
          >
            Utwórz
          </Button>
        </div>
      </div>
    </Modal>
  );
}
